import React, { useState, useEffect } from 'react';
import { 
  Leaf, Droplets, TreeDeciduous, Recycle, Wind, Sun, Phone,
  ChevronDown, ArrowRight, CheckCircle, Sparkles, Globe, Heart
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const SolucionesEcologicasPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const solutions = [
    {
      icon: Sun,
      title: 'Energía Solar Fotovoltaica',
      description: 'Generá tu propia electricidad limpia a partir del sol. La solución más popular y rentable para hogares y empresas.',
      benefits: ['Reducción de hasta 100% en factura eléctrica', 'Vida útil de 25+ años', 'Mínimo mantenimiento']
    },
    {
      icon: Droplets,
      title: 'Calentadores Solares de Agua',
      description: 'Agua caliente gratis usando la energía del sol. Ideal para hogares, hoteles y establecimientos con alto consumo de agua caliente.',
      benefits: ['Ahorro del 80% en gas/electricidad', 'Funciona incluso en días nublados', 'Instalación sencilla']
    },
    {
      icon: Wind,
      title: 'Sistemas con Baterías de Respaldo',
      description: 'Almacená la energía del sol durante el día para usarla de noche o durante cortes de luz.',
      benefits: ['Energía disponible 24/7', 'Protección contra cortes', 'Mayor independencia energética']
    },
    {
      icon: Recycle,
      title: 'Gestión de Residuos Energéticos',
      description: 'Reciclaje responsable de baterías y equipos solares al final de su vida útil.',
      benefits: ['Disposición certificada', 'Trazabilidad completa', 'Compromiso ambiental']
    }
  ];

  const ecoProducts = [
    {
      name: 'Iluminación LED Solar',
      description: 'Luminarias autónomas para jardines, calles y espacios exteriores.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400'
    },
    {
      name: 'Cargadores Solares',
      description: 'Estaciones de carga para celulares y dispositivos, 100% solar.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=400'
    },
    {
      name: 'Refrigeración Solar',
      description: 'Aires acondicionados y refrigeradores de alta eficiencia energética.',
      image: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=400'
    },
    {
      name: 'Electrodomésticos Eficientes',
      description: 'Línea completa de electrodomésticos de bajo consumo energético.',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400'
    }
  ];

  const faqs = [
    {
      question: '¿Qué tan ecológica es la energía solar?',
      answer: 'La energía solar es una de las fuentes más limpias disponibles. Un panel solar promedio evita aproximadamente 1 tonelada de CO₂ por año. Además, los paneles son 95% reciclables al final de su vida útil.'
    },
    {
      question: '¿Los paneles solares son realmente sustentables?',
      answer: 'Sí. Un panel solar produce entre 20 y 30 veces más energía durante su vida útil que la utilizada para fabricarlo. Además, la industria avanza constantemente en procesos de fabricación más limpios.'
    },
    {
      question: '¿Qué pasa con las baterías al final de su vida?',
      answer: 'Trabajamos con procesos de reciclaje certificados. Las baterías de litio son hasta 95% reciclables, y nos aseguramos de darles disposición final responsable.'
    },
    {
      question: '¿Cómo puedo medir mi impacto ambiental?',
      answer: 'Con nuestro sistema de monitoreo podés ver en tiempo real cuánta energía generás y cuánto CO₂ evitás. También te proporcionamos reportes mensuales de tu impacto ambiental.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-green-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
              <Leaf className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Soluciones Ecológicas</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Energía Limpia para un
              <span className="text-[#90EE90]"> Futuro Sustentable</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Soluciones integrales de energía renovable que cuidan el planeta mientras 
              reducen tus costos. Comprometidos con Paraguay y el medio ambiente.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/calculadora')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Conocer mi Impacto
                <ArrowRight size={20} />
              </button>
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesan%20las%20soluciones%20ecológicas"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                <Phone size={20} />
                Contactar
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Nuestras Soluciones
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Tecnología Verde para Cada Necesidad
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <div key={index} className="bg-white p-6 sm:p-8 rounded-2xl border border-zinc-200 hover:shadow-xl transition-all duration-300">
                  <div className="p-3 bg-green-100 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-green-700" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-3">{solution.title}</h3>
                  <p className="text-zinc-600 text-sm mb-4">{solution.description}</p>
                  <ul className="space-y-2">
                    {solution.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                        <CheckCircle className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eco Products */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Productos Ecológicos
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Todo lo que Necesitás para ser Sustentable
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecoProducts.map((product, index) => (
              <div key={index} className="group relative rounded-2xl overflow-hidden">
                <div className="aspect-square">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-lg font-bold text-white mb-1">{product.name}</h3>
                  <p className="text-sm text-zinc-300">{product.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/productos/catalogo')}
              className="px-8 py-4 bg-zinc-900 text-white font-bold rounded-xl hover:bg-zinc-800 transition-colors inline-flex items-center gap-2"
            >
              Ver Catálogo Completo
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
                <Heart className="w-4 h-4 text-[#90EE90]" />
                <span className="text-sm font-medium text-[#90EE90]">Nuestro Compromiso</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                Más que Energía, Responsabilidad Ambiental
              </h2>
              
              <p className="text-zinc-400 mb-8 leading-relaxed">
                En Zenex creemos que cada instalación solar es un paso hacia un Paraguay más limpio. 
                Por eso, no solo vendemos equipos: educamos, concientizamos y acompañamos a nuestros 
                clientes en su camino hacia la sustentabilidad.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: TreeDeciduous, text: 'Plantamos árboles por cada instalación' },
                  { icon: Recycle, text: 'Reciclaje certificado de equipos' },
                  { icon: Globe, text: 'Huella de carbono negativa' },
                  { icon: Sparkles, text: 'Educación ambiental continua' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-[#90EE90] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600"
                alt="Sustentabilidad"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre Sustentabilidad
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-green-800 to-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <Leaf className="w-12 h-12 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Sumate al Cambio
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Cada acción cuenta. Comenzá tu camino hacia la sustentabilidad con Zenex.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20conocer%20más%20sobre%20soluciones%20ecológicas"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-white text-green-800 font-bold rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Contactar Ahora
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default SolucionesEcologicasPage;
